import { useCallback, useEffect, useState } from "react";

import { TRANSLATION_SETTINGS_STORAGE_KEY } from "../constants";
import type { SupportedLocale } from "../types";
import type { TranslationSettings } from "./translationSettingsSchema";
import {
  readTranslationSettings,
  TRANSLATION_SETTINGS_CHANGED_EVENT,
  writeTranslationSettings,
} from "./translationSettingsStorage";

interface UseTranslationSettingsResult {
  settings: TranslationSettings;
  setIsEnabled: (isEnabled: boolean) => void;
  setLocale: (locale: SupportedLocale) => void;
}

export function useTranslationSettings(): UseTranslationSettingsResult {
  const [settings, setSettings] = useState(readTranslationSettings);

  useEffect(() => {
    function handleTranslationSettingsChanged(event: Event) {
      setSettings((event as Event & { detail: TranslationSettings }).detail);
    }

    function handleStorageChanged(event: StorageEvent) {
      if (event.key === TRANSLATION_SETTINGS_STORAGE_KEY) {
        setSettings(readTranslationSettings());
      }
    }

    window.addEventListener(TRANSLATION_SETTINGS_CHANGED_EVENT, handleTranslationSettingsChanged);
    window.addEventListener("storage", handleStorageChanged);

    return () => {
      window.removeEventListener(
        TRANSLATION_SETTINGS_CHANGED_EVENT,
        handleTranslationSettingsChanged
      );
      window.removeEventListener("storage", handleStorageChanged);
    };
  }, []);

  const setIsEnabled = useCallback((isEnabled: boolean) => {
    const currentSettings = readTranslationSettings();
    if (currentSettings.enabled === isEnabled) {
      return;
    }

    writeTranslationSettings({
      ...currentSettings,
      enabled: isEnabled,
      updatedAt: new Date().toISOString(),
    });
  }, []);

  const setLocale = useCallback((locale: SupportedLocale) => {
    const currentSettings = readTranslationSettings();
    if (currentSettings.locale === locale) {
      return;
    }

    writeTranslationSettings({
      ...currentSettings,
      locale,
      updatedAt: new Date().toISOString(),
    });
  }, []);

  return { settings, setIsEnabled, setLocale };
}
